import {businessCalendarDate} from 'src/api/business-time'
import {toApiDateString} from 'src/jobs/job-schedule-format'

import {
    anchorDateFromOffset,
    anchorOffsetFromDate,
    weekOffsetFromDate,
    weekStartFromWeekOffset,
} from '../dispatch-timeline/time-navigation-helpers'
import type {ViewRange} from '../dispatch-timeline/types'

export type DispatchSearch = {
    customer?: string
    yard?: string
    view?: 'month'
    from?: string
}

export type BoardWindow = {viewRange: ViewRange; anchorOffset: number}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

function parseTrimmedString(value: unknown): string | undefined {
    if (typeof value !== 'string') {
        return undefined
    }

    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
}

export function validateDispatchSearch(search: Record<string, unknown>): DispatchSearch {
    const customer = parseTrimmedString(search['customer'])
    const yard = parseTrimmedString(search['yard'])
    const from = parseTrimmedString(search['from'])
    return {
        ...(customer ? {customer} : {}),
        ...(yard ? {yard} : {}),
        ...(search['view'] === 'month' ? {view: 'month' as const} : {}),
        ...(from && ISO_DATE.test(from) ? {from} : {}),
    }
}

export function buildDispatchSearch(
    customerId?: string | null,
    yard?: string | null,
): DispatchSearch {
    const customer = parseTrimmedString(customerId)
    const yardValue = parseTrimmedString(yard)
    return {...(customer ? {customer} : {}), ...(yardValue ? {yard: yardValue} : {})}
}

/** Encodes the board window as an absolute start date. */
export function boardWindowSearch(
    viewRange: ViewRange,
    anchorOffset: number,
    today = businessCalendarDate(),
): Pick<DispatchSearch, 'view' | 'from'> {
    const month = viewRange === 'month'
    if (anchorOffset === 0) {
        return month ? {view: 'month'} : {}
    }

    const first = month
        ? anchorDateFromOffset(anchorOffset, today)
        : weekStartFromWeekOffset(anchorOffset, today)
    return {...(month ? {view: 'month' as const} : {}), from: toApiDateString(first)}
}

export function withBoardWindow(
    search: DispatchSearch,
    {viewRange, anchorOffset}: BoardWindow,
): DispatchSearch {
    return {
        ...buildDispatchSearch(search.customer, search.yard),
        ...boardWindowSearch(viewRange, anchorOffset),
    }
}

export function withCustomerAndYard(
    search: DispatchSearch,
    customerId: string | null | undefined,
    yard: string | null | undefined,
): DispatchSearch {
    return {
        ...buildDispatchSearch(customerId, yard),
        ...(search.view ? {view: search.view} : {}),
        ...(search.from ? {from: search.from} : {}),
    }
}

export function boardWindowFromSearch(
    search: Pick<DispatchSearch, 'view' | 'from'>,
    today = businessCalendarDate(),
): BoardWindow {
    const viewRange: ViewRange = search.view === 'month' ? 'month' : 'week'
    if (!search.from) {
        return {viewRange, anchorOffset: 0}
    }

    return {
        viewRange,
        anchorOffset:
            viewRange === 'month'
                ? anchorOffsetFromDate(search.from, today)
                : weekOffsetFromDate(search.from, today),
    }
}
